import React, { useState, useEffect } from "react";
import Button from "../../../components/Button";
import { confirmDialog } from "../../../utils/confirm";
import {
  CreditCard,
  PlusCircle,
  Pencil,
  Trash2,
  Eye,
  Sparkles,
} from "lucide-react";
import AddPlanModal from "./AddPlanModal";
import ViewPlanModal from "./ViewPlanModal";
import PageLoader from "../../../components/PageLoader";
import Swal from "sweetalert2";
import {
  getSubscriptionPlans,
  deleteSubscriptionPlan,
  updateSubscriptionPlan,
  getCourses,
} from "../../../api";

const AdminSubscriptionPlans = () => {
  const toCycleLabel = (plan) => {
    const cycle = String(
      plan?.billingCycle ?? plan?.planType ?? "",
    ).toLowerCase();
    if (cycle === "annually" || cycle === "annual" || cycle === "yearly")
      return "Annually";
    if (cycle === "monthly" || cycle === "month") return "Monthly";
    const text = String(plan?.price ?? "").toLowerCase();
    if (
      text.includes("/ year") ||
      text.includes("/year") ||
      text.includes("annually") ||
      text.includes("annual")
    ) {
      return "Annually";
    }
    return "Monthly";
  };

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [viewingPlan, setViewingPlan] = useState(null);
  const [courses, setCourses] = useState([]);

  const loadPlans = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError("");
    try {
      const [plansList, coursesList] = await Promise.all([
        getSubscriptionPlans(),
        getCourses().catch(() => []),
      ]);
      setPlans(plansList);
      setCourses(coursesList);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load subscription plans",
      );
      setPlans([]);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setModalOpen(true);
  };

  const handleView = (plan) => {
    setViewingPlan(plan);
  };

  const closeViewModal = () => {
    setViewingPlan(null);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: "Delete plan?",
      text: "This plan will be removed. Users on this plan may need to be updated.",
    });
    if (!confirmed) return;
    try {
      await deleteSubscriptionPlan(id);
      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Subscription plan deleted successfully",
        confirmButtonColor: "#38915c",
      });
      await loadPlans(false);
      if (editingPlan?._id === id) {
        setEditingPlan(null);
        setModalOpen(false);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err.response?.data?.message || err.message || "Failed to delete plan",
        confirmButtonColor: "#38915c",
      });
    }
  };

  const handleToggleAllAccess = async (plan, newValue) => {
    try {
      // Optimistic update
      setPlans((prev) =>
        prev.map((p) =>
          String(p._id) === String(plan._id)
            ? { ...p, isAllAccess: newValue }
            : p,
        ),
      );

      await updateSubscriptionPlan(plan._id, {
        ...plan,
        isAllAccess: newValue,
      });

      Swal.fire({
        icon: "success",
        title: "Updated",
        text: `All-Access ${newValue ? "enabled" : "disabled"} for ${plan.name}`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      // Revert on error
      setPlans((prev) =>
        prev.map((p) =>
          String(p._id) === String(plan._id)
            ? { ...p, isAllAccess: !newValue }
            : p,
        ),
      );
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err.response?.data?.message || err.message || "Failed to update plan",
      });
    }
  };

  const openAddModal = () => {
    setEditingPlan(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingPlan(null);
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-primary-600 mb-1">
            <CreditCard className="w-5 h-5" />
            <span className="text-sm font-medium uppercase tracking-wide">
              Manage
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">
            Subscription plans
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Plans shown on the user Subscription page. Add or edit name, price,
            tag, description, and features.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => loadPlans(true)}>
            Refresh
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={openAddModal}
            className="flex items-center gap-2"
          >
            <PlusCircle className="w-4 h-4" />
            Add plan
          </Button>
        </div>
      </div>

      {/* Prominent Free Plan All-Access Toggle */}
      {plans.some(
        (p) =>
          p.price === "0" ||
          p.name.toLowerCase().includes("free") ||
          p.slug === "free",
      ) && (
        <div className="mb-8 p-6 bg-gradient-to-r from-primary-600 to-primary-700 rounded-3xl shadow-xl shadow-primary-100 text-white flex flex-wrap items-center justify-between gap-6 overflow-hidden relative group">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32 transition-transform group-hover:scale-110 duration-700"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="w-6 h-6 text-yellow-300 animate-pulse" />
              <h2 className="text-xl font-bold">
                Free Plan All-Access (Temporary)
              </h2>
            </div>
            <p className="text-primary-50 max-w-xl text-sm leading-relaxed">
              When enabled, all users on the <strong>Free Plan</strong> will
              instantly get access to every course in the database. Disable this
              to require individual course purchases or upgrades.
            </p>
          </div>
          <div className="relative z-10 flex items-center gap-4 bg-white/10 p-4 rounded-2xl backdrop-blur-sm border border-white/20">
            <span className="text-sm font-bold uppercase tracking-wider">
              {plans.find(
                (p) =>
                  p.price === "0" ||
                  p.name.toLowerCase().includes("free") ||
                  p.slug === "free",
              )?.isAllAccess
                ? "Active"
                : "Inactive"}
            </span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={
                  !!plans.find(
                    (p) =>
                      p.price === "0" ||
                      p.name.toLowerCase().includes("free") ||
                      p.slug === "free",
                  )?.isAllAccess
                }
                onChange={(e) => {
                  const freePlan = plans.find(
                    (p) =>
                      p.price === "0" ||
                      p.name.toLowerCase().includes("free") ||
                      p.slug === "free",
                  );
                  if (freePlan)
                    handleToggleAllAccess(freePlan, e.target.checked);
                }}
              />
              <div className="w-14 h-7 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-yellow-400"></div>
            </label>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-600 text-sm">
          {error}
        </div>
      )}

      <AddPlanModal
        open={modalOpen}
        onClose={closeModal}
        onSuccess={() => loadPlans(false)}
        editPlan={editingPlan}
      />

      <ViewPlanModal
        open={Boolean(viewingPlan)}
        onClose={closeViewModal}
        plan={viewingPlan}
        courses={courses}
        onEdit={handleEdit}
      />

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Name
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Price
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Type
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Tag
                </th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">
                  All Access
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Features
                </th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {plans.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-500">
                    No plans yet. Click &quot;Add plan&quot; to create one.
                  </td>
                </tr>
              ) : (
                plans.map((plan) => (
                  <tr
                    key={plan._id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-3 px-4 font-medium">
                      {plan.name}
                      {plan.isAllAccess && (
                        <span className="ml-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-primary-100 text-primary-700">
                          All Access
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-600">{plan.price}</td>
                    <td className="py-3 px-4 text-gray-600 text-sm capitalize">
                      {toCycleLabel(plan)}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-sm">
                      {plan.tag || "—"}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <input
                        type="checkbox"
                        checked={!!plan.isAllAccess}
                        onChange={(e) =>
                          handleToggleAllAccess(plan, e.target.checked)
                        }
                        className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 cursor-pointer"
                        title={
                          plan.isAllAccess
                            ? "Disable All Access"
                            : "Enable All Access"
                        }
                      />
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-sm">
                      {(plan.features || []).length} item(s)
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <button
                          type="button"
                          onClick={() => handleView(plan)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-secondary-600 hover:bg-secondary-50 hover:text-secondary-700 transition-colors"
                          aria-label="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEdit(plan)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                          aria-label="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(plan._id)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                          aria-label="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminSubscriptionPlans;
